/**
 * Fabric.js Configuration
 *
 * This module configures Fabric.js for the DesignDeck application.
 * It MUST be imported at the top of main.tsx BEFORE any Fabric.js usage.
 *
 * Fabric.js 6.x Change:
 * - toJSON(customProperties) argument is NO LONGER SUPPORTED
 * - Custom properties must be set globally via FabricObject.customProperties
 *
 * @see https://fabricjs.com/docs/using-custom-properties/
 */

import { FabricObject } from 'fabric';

// =============================================================================
// TYPESCRIPT INTERFACE EXTENSIONS
// =============================================================================

/**
 * Extend Fabric.js interfaces to include our custom properties.
 * This provides type-safety when accessing custom properties on Fabric objects.
 */
declare module 'fabric' {
  interface FabricObject {
    // Object identification
    id?: string;
    name?: string;

    // Asset management
    assetId?: string;
    assetType?: 'local' | 's3';
    originalUrl?: string;
    _uploadStatus?: 'pending' | 'completed' | 'failed';

    // Personalization
    isPersonalizable?: boolean;
    personalizationKey?: string;
    personalizationConfig?: Record<string, unknown>;

    // Locking/editing
    isLocked?: boolean;
    isEditable?: boolean;

    // Scale constraints
    minScale?: number;
    maxScale?: number;

    // Embroidery: which threads the motif uses, and where its source lives.
    // The rendered artwork is not color-separable — hatching and drop shadow put
    // hundreds of RGB values into the PNG — so the mapping has to travel with
    // the object. Storing it here rather than in the store also keeps it in sync
    // through undo/redo and product switches.
    confirmedMapping?: Record<string, string>;
    embFileKey?: string;

    // Custom data storage
    customData?: Record<string, unknown>;
  }

  interface SerializedObjectProps {
    id?: string;
    name?: string;
    assetId?: string;
    assetType?: string;
    originalUrl?: string;
    _uploadStatus?: string;
    confirmedMapping?: Record<string, string>;
    embFileKey?: string;
    isPersonalizable?: boolean;
    personalizationKey?: string;
    personalizationConfig?: Record<string, unknown>;
    isLocked?: boolean;
    isEditable?: boolean;
    minScale?: number;
    maxScale?: number;
    customData?: Record<string, unknown>;
  }
}

// =============================================================================
// CUSTOM PROPERTIES CONFIGURATION
// =============================================================================

/**
 * All custom properties that should be serialized with Fabric.js objects.
 * These are automatically included in toJSON() and toObject() output.
 *
 * Categories:
 * - Object identification: id, name
 * - Asset management: assetId, assetType, originalUrl, _uploadStatus
 * - Personalization: isPersonalizable, personalizationKey, personalizationConfig
 * - Locking/editing: isLocked, isEditable
 * - Scale constraints: minScale, maxScale
 * - Custom data: customData
 */
export const FABRIC_CUSTOM_PROPERTIES = [
  // Object identification
  'id',
  'name',

  // Asset management (for CDN/S3 images)
  'assetId',
  'assetType',
  'originalUrl',
  '_uploadStatus',

  // Embroidery thread mapping (Fabric serializes nothing outside this list)
  'confirmedMapping',
  'embFileKey',

  // Personalization features
  'isPersonalizable',
  'personalizationKey',
  'personalizationConfig',

  // Locking/editing controls
  'isLocked',
  'isEditable',

  // Scale constraints
  'minScale',
  'maxScale',

  // Generic custom data storage
  'customData',
] as const;

export type FabricCustomProperty = (typeof FABRIC_CUSTOM_PROPERTIES)[number];

// =============================================================================
// FABRIC.JS GLOBAL CONFIGURATION
// =============================================================================

/**
 * Set custom properties globally on FabricObject.
 * This is the Fabric.js 6.x way to ensure properties are serialized.
 *
 * IMPORTANT: This assignment must happen BEFORE any Fabric.js objects are created.
 */
FabricObject.customProperties = [...FABRIC_CUSTOM_PROPERTIES];

// =============================================================================
// LEGACY EXPORT (for backwards compatibility during migration)
// =============================================================================

/**
 * @deprecated Use FABRIC_CUSTOM_PROPERTIES instead.
 * This export is kept for backwards compatibility during migration.
 */
export const CANVAS_CUSTOM_PROPERTIES = FABRIC_CUSTOM_PROPERTIES;
